import ApplicationController from './application_controller'
import { useThrottle } from 'stimulus-use'
import WaveSurfer from 'wavesurfer.js'

const PLAY_BUTTON = '.click_player_play'

function currentAudio() {
  const player = document.getElementById('audio-player')
  const audio = player?.querySelector('audio')
  return audio ? { id: player.dataset.id, audio } : null
}

// Track list play buttons mirror the player: the open track's button is
// highlighted, and shows pause while it's playing
function syncPlayButtons() {
  const current = currentAudio()
  document.querySelectorAll(PLAY_BUTTON).forEach(button => {
    const isCurrent = !!current && button.dataset.id === current.id
    const playing = isCurrent && !current.audio.paused
    button.classList.toggle('is-current', isCurrent)
    button.querySelector('.mdi')?.classList.toggle('mdi-play-circle-outline', !playing)
    button.querySelector('.mdi')?.classList.toggle('mdi-pause-circle-outline', playing)
    button.setAttribute('aria-label', playing ? 'Pause track preview' : 'Play track preview')
    button._tippy?.setContent(playing ? 'Pause' : 'Play')
  })
}

// Clicking the open track's button pauses/resumes it rather than reloading
// the player. Capture phase, so it runs before StimulusReflex's click handler.
document.addEventListener('click', event => {
  const button = event.target.closest?.(PLAY_BUTTON)
  const current = currentAudio()
  if (!button || !current || button.dataset.id !== current.id) return

  event.preventDefault()
  event.stopPropagation()
  current.audio.paused ? current.audio.play() : current.audio.pause()
}, true)

export default class extends ApplicationController {

  static targets = ['audio', 'waveform', 'toggleIcon', 'toggle', 'time']
  static values = { peaks: Array, duration: Number }
  static throttles = ['time']

  connect() {
    super.connect()
    useThrottle(this, { wait: 10000 })

    this.lastTime = 0;

    if(this.hasAudioTarget) {
      this.audioTarget.oncanplay = () => {
        this.lastTime = Math.round(this.audioTarget.currentTime)
      }

      this.audioTarget.onseeked = () => {
        this.stimulate("Player#skip");
        this.lastTime = Math.round(this.audioTarget.currentTime)
      }

      this.audioTarget.ontimeupdate = this.time.bind(this);
      this.audioTarget.addEventListener('play', syncPlayButtons)
      this.audioTarget.addEventListener('pause', syncPlayButtons)

      if (this.hasWaveformTarget && this.peaksValue.length) this.renderWaveform()
    }

    // Also runs as each play button connects, e.g. after a list re-renders
    syncPlayButtons()
  }

  disconnect() {
    this.wavesurfer?.destroy()
    this.wavesurfer = null
    // Clears the highlight once the player has closed
    if (this.hasAudioTarget) syncPlayButtons()
  }

  // Draws the stored peaks over the existing <audio> element, so the
  // waveform shows instantly (no need to download/decode the file first)
  // and the stats hooks above keep working. Click/drag to seek.
  renderWaveform() {
    const style = getComputedStyle(this.waveformTarget)
    this.wavesurfer = WaveSurfer.create({
      container: this.waveformTarget,
      media: this.audioTarget,
      url: this.audioTarget.getAttribute('src'),
      peaks: [this.peaksValue],
      duration: this.durationValue,
      height: 36,
      barWidth: 2,
      barGap: 1,
      barRadius: 0,
      cursorWidth: 0,
      waveColor: style.getPropertyValue('--wave-color').trim() || '#58585b',
      progressColor: style.color,
      cursorColor: style.color,
      dragToSeek: true,
      normalize: true,
    })

    const sync = () => this.updateControls()
    this.audioTarget.addEventListener('play', sync)
    this.audioTarget.addEventListener('pause', sync)
    this.audioTarget.addEventListener('timeupdate', sync)
    this.updateControls()
  }

  // Keyboard seeking on the waveform slider
  seekByKey(event) {
    const steps = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5, PageDown: -30, PageUp: 30 }
    const audio = this.audioTarget
    const duration = audio.duration || this.durationValue
    let time
    if (event.key in steps) time = audio.currentTime + steps[event.key]
    else if (event.key === 'Home') time = 0
    else if (event.key === 'End') time = duration - 1
    else return

    event.preventDefault()
    audio.currentTime = Math.min(Math.max(time, 0), duration)
    this.updateControls()
  }

  toggle(event) {
    event.preventDefault()
    this.audioTarget.paused ? this.audioTarget.play() : this.audioTarget.pause()
  }

  updateControls() {
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.classList.toggle('mdi-play', this.audioTarget.paused)
      this.toggleIconTarget.classList.toggle('mdi-pause', !this.audioTarget.paused)
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute('aria-label', this.audioTarget.paused ? 'Play' : 'Pause')
    }
    if (this.hasWaveformTarget) {
      const now = Math.floor(this.audioTarget.currentTime || 0)
      this.waveformTarget.setAttribute('aria-valuenow', now)
      this.waveformTarget.setAttribute('aria-valuetext', `${formatTime(now)} of ${formatTime(this.audioTarget.duration || this.durationValue)}`)
    }
    if (this.hasTimeTarget) {
      this.timeTarget.textContent = `${formatTime(this.audioTarget.currentTime)} / ${formatTime(this.audioTarget.duration || this.durationValue)}`
    }
    this.element.classList.toggle('audio-player--playing', !this.audioTarget.paused)
  }

  time() {
    if(this.hasAudioTarget) {
      if(!this.audioTarget.paused) {
        var currentTime = Math.round(this.audioTarget.currentTime)
        var delta = currentTime - this.lastTime;
        this.stimulate("Player#time", delta);
        this.lastTime = currentTime
      }
    }

  }

  next(event) {
    const trackId = this.element.dataset.id;
    const nowPlayingButton = $(`.click_player_play[data-id='${trackId}']`);
    var nextPlayButton = nowPlayingButton.parents('.track').next().find('.click_player_play');
    if (nextPlayButton[0]) {
      this.stimulate("Player#play", nextPlayButton[0]);
    } else {
      var firstPlayButton = $('.click_player_play').first();
      this.stimulate("Player#play", firstPlayButton[0]);
    }
    event.preventDefault();
  }

  previous(event) {
    const trackId = this.element.dataset.id;
    const nowPlayingButton = $(`.click_player_play[data-id='${trackId}']`);
    const prevPlayButton = nowPlayingButton.parents('.track').prev().find('.click_player_play');
    if (prevPlayButton[0]) {
      this.stimulate("Player#play", prevPlayButton[0]);
    } else {
      var lastPlayButton = $('.click_player_play').last();
      this.stimulate("Player#play", lastPlayButton[0]);
    }
    event.preventDefault();
  }

  playSuccess(element, reflex, noop, reflexId) {
    $('audio').each(function () {
      this.play();
    });
  }

  close() {
    this.element.parentNode.removeChild(this.element);
  }

}

function formatTime(seconds) {
  const s = Math.floor(seconds || 0)
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}